/**
 * Databricks Connection Form (DC-1163)
 *
 * Form for creating and editing Databricks connections.
 * Supports PAT and OAuth2 authentication methods.
 *
 * @see Documentation/Development/Design Docs/Sprint 13/Unified_Integration_Architecture.md
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import './DatabricksConnectionForm.css';

// ============================================================================
// TYPES
// ============================================================================

interface DatabricksConnectionConfig {
  workspaceUrl: string;
  warehouseId?: string;
  defaultCatalog?: string;
  defaultSchema?: string;
  cloudProvider: 'azure' | 'aws' | 'gcp';
  mlflowEnabled: boolean;
  httpPath?: string;
}

interface FormData {
  name: string;
  description: string;
  endpointUrl: string;
  authType: 'pat' | 'oauth2';
  token?: string;
  clientId?: string;
  clientSecret?: string;
  connectionConfig: DatabricksConnectionConfig;
}

interface DatabricksConnectionFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<FormData>;
  isEditing?: boolean;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_CONFIG: DatabricksConnectionConfig = {
  workspaceUrl: '',
  cloudProvider: 'azure',
  mlflowEnabled: true,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const DatabricksConnectionForm: React.FC<DatabricksConnectionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isEditing = false,
}) => {
  const { accessToken: _token } = useAuth();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    endpointUrl: initialData?.endpointUrl || '',
    authType: initialData?.authType || 'pat',
    token: '',
    clientId: '',
    clientSecret: '',
    connectionConfig: {
      ...DEFAULT_CONFIG,
      ...initialData?.connectionConfig,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;

      if (name.startsWith('config.')) {
        const configKey = name.replace('config.', '');
        setFormData((prev) => ({
          ...prev,
          connectionConfig: {
            ...prev.connectionConfig,
            [configKey]: type === 'checkbox' ? checked : value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value,
        }));
      }

      // Clear error for this field
      if (errors[name]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    },
    [errors]
  );

  const handleWorkspaceUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const url = e.target.value.replace(/\/+$/, '').trim();
      setFormData((prev) => ({
        ...prev,
        endpointUrl: url,
        connectionConfig: {
          ...prev.connectionConfig,
          workspaceUrl: url,
        },
      }));

      // Clear error
      if (errors.endpointUrl) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.endpointUrl;
          return next;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Connection name is required';
    }

    if (!formData.endpointUrl.trim()) {
      newErrors.endpointUrl = 'Workspace URL is required';
    } else if (!formData.endpointUrl.startsWith('https://')) {
      newErrors.endpointUrl = 'Workspace URL must start with https://';
    }

    // Auth-specific validation
    if (formData.authType === 'pat') {
      if (!formData.token?.trim()) {
        newErrors.token = 'Personal Access Token is required';
      }
    } else if (formData.authType === 'oauth2') {
      if (!formData.clientId?.trim()) {
        newErrors.clientId = 'Client ID is required';
      }
      if (!formData.clientSecret?.trim()) {
        newErrors.clientSecret = 'Client Secret is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) return;

      setIsSubmitting(true);

      try {
        await onSubmit(formData);
      } catch (error) {
        console.error('Failed to save connection:', error);
        setErrors({
          submit: error instanceof Error ? error.message : 'Failed to save connection',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, onSubmit]
  );

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <form className="databricks-connection-form" onSubmit={handleSubmit}>
      {/* Connection Name */}
      <div className="form-group">
        <label htmlFor="name">Connection Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="e.g., Production Databricks"
          className={errors.name ? 'error' : ''}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Optional description of this connection"
          rows={2}
        />
      </div>

      {/* Workspace URL */}
      <div className="form-group">
        <label htmlFor="endpointUrl">Workspace URL *</label>
        <input
          type="url"
          id="endpointUrl"
          name="endpointUrl"
          value={formData.endpointUrl}
          onChange={handleWorkspaceUrlChange}
          placeholder="https://your-workspace.cloud.databricks.com"
          className={errors.endpointUrl ? 'error' : ''}
        />
        {errors.endpointUrl && <span className="error-message">{errors.endpointUrl}</span>}
        <span className="hint">The URL of your Databricks workspace</span>
      </div>

      {/* Cloud Provider */}
      <div className="form-group">
        <label htmlFor="config.cloudProvider">Cloud Provider *</label>
        <select
          id="config.cloudProvider"
          name="config.cloudProvider"
          value={formData.connectionConfig.cloudProvider}
          onChange={handleInputChange}
        >
          <option value="azure">Microsoft Azure</option>
          <option value="aws">Amazon Web Services</option>
          <option value="gcp">Google Cloud Platform</option>
        </select>
      </div>

      {/* Authentication Section */}
      <fieldset className="auth-section">
        <legend>Authentication</legend>

        {/* Auth Type */}
        <div className="form-group">
          <label htmlFor="authType">Authentication Method *</label>
          <select
            id="authType"
            name="authType"
            value={formData.authType}
            onChange={handleInputChange}
          >
            <option value="pat">Personal Access Token (PAT)</option>
            <option value="oauth2">OAuth 2.0 (Service Principal)</option>
          </select>
        </div>

        {/* PAT Token */}
        {formData.authType === 'pat' && (
          <div className="form-group">
            <label htmlFor="token">Personal Access Token *</label>
            <input
              type="password"
              id="token"
              name="token"
              value={formData.token}
              onChange={handleInputChange}
              placeholder="dapi-xxxxxxxxxxxxxxxx"
              className={errors.token ? 'error' : ''}
            />
            {errors.token && <span className="error-message">{errors.token}</span>}
            <span className="hint">
              Generate a PAT in Databricks: User Settings → Access Tokens
            </span>
          </div>
        )}

        {/* OAuth2 Credentials */}
        {formData.authType === 'oauth2' && (
          <>
            <div className="form-group">
              <label htmlFor="clientId">Client ID *</label>
              <input
                type="text"
                id="clientId"
                name="clientId"
                value={formData.clientId}
                onChange={handleInputChange}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className={errors.clientId ? 'error' : ''}
              />
              {errors.clientId && <span className="error-message">{errors.clientId}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="clientSecret">Client Secret *</label>
              <input
                type="password"
                id="clientSecret"
                name="clientSecret"
                value={formData.clientSecret}
                onChange={handleInputChange}
                placeholder="Enter client secret"
                className={errors.clientSecret ? 'error' : ''}
              />
              {errors.clientSecret && <span className="error-message">{errors.clientSecret}</span>}
            </div>
            <span className="hint">
              Create a Service Principal in Azure AD or AWS IAM
            </span>
          </>
        )}
      </fieldset>

      {/* Optional Configuration */}
      <fieldset className="config-section">
        <legend>Optional Configuration</legend>

        {/* SQL Warehouse ID */}
        <div className="form-group">
          <label htmlFor="config.warehouseId">SQL Warehouse ID</label>
          <input
            type="text"
            id="config.warehouseId"
            name="config.warehouseId"
            value={formData.connectionConfig.warehouseId || ''}
            onChange={handleInputChange}
            placeholder="xxxxxxxxxxxxxxxx"
          />
          <span className="hint">Required for SQL execution and data preview</span>
        </div>

        {/* Default Catalog */}
        <div className="form-group">
          <label htmlFor="config.defaultCatalog">Default Catalog</label>
          <input
            type="text"
            id="config.defaultCatalog"
            name="config.defaultCatalog"
            value={formData.connectionConfig.defaultCatalog || ''}
            onChange={handleInputChange}
            placeholder="main"
          />
        </div>

        {/* Default Schema */}
        <div className="form-group">
          <label htmlFor="config.defaultSchema">Default Schema</label>
          <input
            type="text"
            id="config.defaultSchema"
            name="config.defaultSchema"
            value={formData.connectionConfig.defaultSchema || ''}
            onChange={handleInputChange}
            placeholder="default"
          />
        </div>

        {/* MLflow Enabled */}
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="config.mlflowEnabled"
              checked={formData.connectionConfig.mlflowEnabled}
              onChange={handleInputChange}
            />
            <span>Enable MLflow Integration</span>
          </label>
          <span className="hint">Access registered ML models and experiments</span>
        </div>
      </fieldset>

      {/* Submit Error */}
      {errors.submit && (
        <div className="submit-error">{errors.submit}</div>
      )}

      {/* Actions */}
      <div className="form-actions">
        <span className="hint">
          Credentials are verified after saving — use Test Connection on the connection row.
        </span>
        <div className="spacer" />
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEditing ? 'Update Connection' : 'Create Connection'}
        </button>
      </div>
    </form>
  );
};

export default DatabricksConnectionForm;
